import React, { useState } from 'react';
import { Search } from 'lucide-react';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Money = ({ value, redIfPositive, greenIfPositive }) => {
  const num = parseFloat(value || 0);
  if (num === 0) return <span className="text-gray-300">—</span>;
  let cls = 'text-gray-700';
  if (greenIfPositive && num > 0) cls = 'font-semibold text-green-700';
  if (redIfPositive  && num > 0) cls = 'font-semibold text-red-700';
  return <span className={cls}>RD$ {fmt(num)}</span>;
};

export default function DetalleTab({ detalles }) {
  const [search, setSearch] = useState('');

  const filtered = detalles.filter(d => {
    const q = search.toLowerCase();
    return (
      (d.apellidos_nombres || '').toLowerCase().includes(q) ||
      (d.rnc_cedula        || '').toLowerCase().includes(q)
    );
  });

  // Footer totals
  const totals = detalles.reduce((acc, d) => {
    acc.sueldos   += parseFloat(d.sueldos_pagados              || 0);
    acc.otras     += parseFloat(d.otras_remuneraciones         || 0);
    acc.total     += parseFloat(d.total_pagado                 || 0);
    acc.ss        += parseFloat(d.retencion_seguridad_social   || 0);
    acc.isrRet    += parseFloat(d.impuesto_retenido            || 0);
    acc.isrLiq    += parseFloat(d.impuesto_liquidado           || 0);
    acc.saldo     += parseFloat(d.saldo_favor                  || 0);
    acc.monto     += parseFloat(d.monto_pagar                  || 0);
    return acc;
  }, { sueldos: 0, otras: 0, total: 0, ss: 0, isrRet: 0, isrLiq: 0, saldo: 0, monto: 0 });

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nombre o cédula…"
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider w-8">#</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider min-w-[160px]">Apellidos y Nombres</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">RNC/Cédula</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">Sueldos</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">Otras Rem.</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">Total Pagado</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">Ret. SS</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">ISR Retenido</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">ISR Liquidado</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">Saldo Favor</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">Por Pagar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-10 text-center text-sm text-gray-400">
                  {search ? 'No se encontraron resultados' : 'Sin datos de empleados'}
                </td>
              </tr>
            ) : filtered.map((d, i) => (
              <tr key={d.employee_id || i} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2.5 text-xs text-gray-400">{i + 1}</td>
                <td className="px-3 py-2.5">
                  <p className="font-medium text-gray-900 text-xs">{d.apellidos_nombres}</p>
                  {d.empleado_codigo && <p className="text-[10px] text-gray-400">{d.empleado_codigo}</p>}
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-600">{d.rnc_cedula || <span className="text-red-400">Sin cédula</span>}</td>
                <td className="px-3 py-2.5 text-right text-xs"><Money value={d.sueldos_pagados} /></td>
                <td className="px-3 py-2.5 text-right text-xs"><Money value={d.otras_remuneraciones} /></td>
                <td className="px-3 py-2.5 text-right text-xs font-medium text-gray-900">RD$ {fmt(d.total_pagado)}</td>
                <td className="px-3 py-2.5 text-right text-xs"><Money value={d.retencion_seguridad_social} /></td>
                <td className="px-3 py-2.5 text-right text-xs"><Money value={d.impuesto_retenido} /></td>
                <td className="px-3 py-2.5 text-right text-xs"><Money value={d.impuesto_liquidado} /></td>
                <td className="px-3 py-2.5 text-right text-xs"><Money value={d.saldo_favor} greenIfPositive /></td>
                <td className="px-3 py-2.5 text-right text-xs"><Money value={d.monto_pagar} redIfPositive /></td>
              </tr>
            ))}
          </tbody>
          {detalles.length > 0 && (
            <tfoot>
              <tr className="bg-blue-50 border-t-2 border-blue-200 font-bold text-xs">
                <td />
                <td className="px-3 py-3 text-gray-700 uppercase tracking-wider">Totales</td>
                <td />
                <td className="px-3 py-3 text-right text-gray-900">RD$ {fmt(totals.sueldos)}</td>
                <td className="px-3 py-3 text-right text-gray-900">RD$ {fmt(totals.otras)}</td>
                <td className="px-3 py-3 text-right text-gray-900">RD$ {fmt(totals.total)}</td>
                <td className="px-3 py-3 text-right text-gray-900">RD$ {fmt(totals.ss)}</td>
                <td className="px-3 py-3 text-right text-gray-900">RD$ {fmt(totals.isrRet)}</td>
                <td className="px-3 py-3 text-right text-gray-900">RD$ {fmt(totals.isrLiq)}</td>
                <td className="px-3 py-3 text-right text-green-700">RD$ {fmt(totals.saldo)}</td>
                <td className="px-3 py-3 text-right text-red-700">RD$ {fmt(totals.monto)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
